// Client helpers for LinkedIn OAuth + posting.
import { supabase } from "@/integrations/supabase/client";

// Public OAuth client ID — safe to ship in frontend (the secret stays server-side).
export const LINKEDIN_CLIENT_ID = "863epalw1m09la";

const STATE_KEY = "wekit_linkedin_oauth_state";
const RETURN_TO_KEY = "wekit_linkedin_return_to";

// Scopes:
//   openid, profile, email  -> Sign in with LinkedIn (always granted)
//   w_member_social         -> Post to LinkedIn (requires LinkedIn app review)
export const LINKEDIN_SCOPES = "openid profile email w_member_social";

export function getLinkedInRedirectUri(): string {
  return `${window.location.origin}/auth/linkedin/callback`;
}

export function buildLinkedInAuthUrl(clientId: string, returnTo = "/settings"): string {
  const state = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    sessionStorage.setItem(STATE_KEY, state);
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  } catch {
    /* ignore */
  }

  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: getLinkedInRedirectUri(),
    state,
    scope: LINKEDIN_SCOPES,
  });
  return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
}

export function readStoredState(): string | null {
  try {
    return sessionStorage.getItem(STATE_KEY);
  } catch {
    return null;
  }
}

export function readReturnTo(): string {
  try {
    return sessionStorage.getItem(RETURN_TO_KEY) || "/settings";
  } catch {
    return "/settings";
  }
}

export function clearOAuthState() {
  try {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* ignore */
  }
}

export async function exchangeLinkedInCode(code: string) {
  return supabase.functions.invoke("linkedin-oauth-exchange", {
    body: { code, redirect_uri: getLinkedInRedirectUri() },
  });
}

export async function postToLinkedIn(text: string, url?: string) {
  return supabase.functions.invoke("linkedin-post", { body: { text, url } });
}

export async function getMyLinkedInConnection() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from("linkedin_connections")
    .select("linkedin_sub, profile, scope, expires_at, connected_at")
    .eq("user_id", user.id)
    .maybeSingle();
  return data;
}

export async function disconnectLinkedIn() {
  // Server-side: revokes the access token at LinkedIn, deletes our row, writes audit log.
  const { data, error } = await supabase.functions.invoke("linkedin-disconnect", { body: {} });
  if (error) return { error: error.message, revoked: null as null | string };
  if (!(data as { ok?: boolean })?.ok) {
    return { error: (data as { error?: string })?.error ?? "disconnect_failed", revoked: null };
  }
  return { error: null, revoked: (data as { revoked: string }).revoked };
}

export type LinkedInRefreshResult =
  | { ok: true; expires_at: string }
  | { ok: false; reason: string; expires_at?: string };

export async function refreshLinkedInToken(): Promise<LinkedInRefreshResult> {
  const { data, error } = await supabase.functions.invoke("linkedin-refresh-token", { body: {} });
  if (error) return { ok: false, reason: error.message };
  const d = data as Partial<LinkedInRefreshResult> & { ok?: boolean };
  if (d?.ok === true && typeof (d as { expires_at?: string }).expires_at === "string") {
    return { ok: true, expires_at: (d as { expires_at: string }).expires_at };
  }
  return { ok: false, reason: (d as { reason?: string })?.reason ?? "unknown_error" };
}

export type LinkedInAuditEvent = {
  id: string;
  event: "connected" | "disconnected" | "refreshed" | "revoked" | "imported" | "error";
  status: "success" | "failure";
  detail: string | null;
  created_at: string;
};

export async function getMyLinkedInAuditLog(limit = 15): Promise<LinkedInAuditEvent[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data } = await supabase
    .from("linkedin_audit_log")
    .select("id, event, status, detail, created_at")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false })
    .limit(limit);
  return (data ?? []) as LinkedInAuditEvent[];
}
